import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Ripple } from "primereact/ripple";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { Image } from "primereact/image";
import { InputText } from "primereact/inputtext";

import NoDataCreated from "../../../../../Components/NoDataCreated";
import TextInput from "../../../../../Components/TextInput";
import ErrorPage from "../../../../../Components/ErrorPage";
import DocumentViewModal from "../../../../../Components/DocumentViewModal";
import { goToTop } from "../../../../../Components/goToTop";

import { baseURL } from "../../../../../Api/ApiConstant";
import { getLoginUserRoleName, UserRoleName } from "../../../../../Utils/LoginUserRoleName";
import { formatDate } from "../../../../../Utils/getDate";


import { useAppDispatch, useAppSelector } from "../../../../../Controllers/hooks";
import { getAllPlanChckTrackerList } from "../../../../../Controllers/Thunks/PlanCheckTrackerThunks";
import { checkEmail, checkText } from "../../../../../Utils/Validator";
import { createPlanCheckTrackerResponse, sharePlanCheck, signAndPublishPlanCheckTrackerByArc, signAndPublishPlanCheckTrackerByBI } from "../../../../../Api/PlanCheck/PlanCheckApiServices";
import { PlanCheckTracker, PlanCheckTrackerPart, PlanCheckTrackerItemList } from "../../../../../Controllers/Types/PlanCheck/PlanCheckTrackerType";
import { Message } from "primereact/message";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";

const PlanCheckTrackerReport: React.FC = () => {
    const toast = useRef<Toast>(null);
    const today = new Date();
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState<boolean>(false);
    const projectData = location.state?.projectData;
    const serviceId = Number(projectData.folder_id);
    const loadingPage = useAppSelector((state) => state?.planCheckTrackerList?.loader);
    const error = useAppSelector((state) => state?.planCheckTrackerList?.errorDisplay);
    const [loginUserRoleName, setLoginUserRoleName] = useState<UserRoleName | ''>('');

    const [planCheckTrackerData, setPlanCheckTrackerData] = useState<PlanCheckTracker | null>(null);

    const [planCheckTrackerPartData, setPlanCheckTrackerPartData] = useState<PlanCheckTrackerPart[]>([]);
    const [planCheckTrackerItemData, setPlanCheckTrackerItemData] = useState<PlanCheckTrackerItemList[]>([]);
    const [showItemDocViewModal, setShowItemDocViewModal] = useState<boolean>(false);
    const [fileUrl, setFileUrl] = useState<string>('');

    const [isReportSigned, setIsReportSigned] = useState<boolean>(false);

    const [isDownloading, setIsDownloading] = useState<boolean>(false);

    const [isSignedAndPublishedByArc, setIsSignedAndPublishedByArc] = useState<boolean>(false);
    const [isSignedAndPublishedByBi, setIsSignedAndPublishedByBi] = useState<boolean>(false);

    /* Sign by architect */
    const [showArcSignatureModal, setShowArcSignatureModal] = useState<boolean>(false);
    const [arcSignature, setArcSignature] = useState<string>("");
    const [arcSignatureError, setArcSignatureError] = useState<string>("");

    /* Sign by building inspector */
    const [showBiSignatureModal, setShowBiSignatureModal] = useState<boolean>(false);
    const [biSignature, setBiSignature] = useState<string>("");
    const [biSignatureError, setBiSignatureError] = useState<string>("");

    const [showMailModal, setShowMailModal] = useState<boolean>(false);
    const [emailAddress, setEmailAddress] = useState<string>('');
    const [emailAddressError, setEmailAddressError] = useState<string>('');

    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) => hasPermission(state, screen, permission));
    };

    const hasPermissionAddSignature = useCanEdit('Plan Check Tracker', 'add_signature');
    const hasPermissionDownloadFile = useCanEdit('Plan Check Tracker', 'download_file');

    const dispatch = useAppDispatch();

    // for fetching data fro api tigger with state object actions
    useEffect(() => {
        planCheckTrackerDataGet();
    }, [location.state]);

    const planCheckTrackerDataGet = async () => {
        await dispatch(getAllPlanChckTrackerList(serviceId));
    }

    // this is get data from state list and set projectlist
    const plancheckTrackerList = useAppSelector((state) => state?.planCheckTrackerList?.planCheckTrackerList);

    useEffect(() => {
        if (location.state && location.state.planCheckTrackerData) {
            const options = plancheckTrackerList && plancheckTrackerList.length > 0;
            const data = location.state.planCheckTrackerData as PlanCheckTracker;

            if (options) {
                const collect = plancheckTrackerList.filter((ex) => ex.id === data.id);
                if (collect && collect.length > 0) {
                    setPlanCheckTrackerData(collect[0]);
                }
            }
        }
    }, [location.state, plancheckTrackerList]);

    useEffect(() => {
        if (planCheckTrackerData && planCheckTrackerData !== null) {
            if (planCheckTrackerData?.is_sign_report_by_arc && planCheckTrackerData?.is_published_by_arc) {
                setIsSignedAndPublishedByArc(true);
            } else {
                setIsSignedAndPublishedByArc(false);
            }

            if (planCheckTrackerData?.is_sign_report_by_bi && planCheckTrackerData?.is_published_by_bi) {
                setIsSignedAndPublishedByBi(true);
            } else {
                setIsSignedAndPublishedByBi(false);
            }
        } else {
            setIsSignedAndPublishedByArc(false);
            setIsSignedAndPublishedByBi(false);
        }
    }, [planCheckTrackerData]);

    useEffect(() => {
        const fetchUserRole = async () => {
            const roleName = await getLoginUserRoleName();
            setLoginUserRoleName(roleName);
        };

        fetchUserRole();
    }, []);

    useEffect(() => {
        if (planCheckTrackerData && planCheckTrackerData !== null) {
            if (planCheckTrackerData?.check_type === 'Detail' && planCheckTrackerData?.plan_check_part && planCheckTrackerData?.plan_check_part?.length > 0) {
                setPlanCheckTrackerPartData(planCheckTrackerData?.plan_check_part);
            } else {
                setPlanCheckTrackerPartData([]);
            }

            if (planCheckTrackerData?.check_type === 'Basic' && planCheckTrackerData?.item_list && planCheckTrackerData?.item_list?.length > 0) {
                setPlanCheckTrackerItemData(planCheckTrackerData?.item_list);
            } else {
                setPlanCheckTrackerItemData([]);
            }
            setIsReportSigned(planCheckTrackerData?.is_sign_report_by_bi === 1);
        } else {
            setPlanCheckTrackerPartData([]);
            setPlanCheckTrackerItemData([]);
            setIsReportSigned(false);
        }
    }, [planCheckTrackerData]);



    /* ********************************* Sign & Publish ********************************* */
    /* *********** Architect *********** */
    const handleCloseArcSignatureModal = () => {
        setShowArcSignatureModal(false);
        setArcSignature("");
        setArcSignatureError('');
    };

    const handleSignAndPublishByARC = () => {
        const planCheckTrackerType = planCheckTrackerData?.check_type;
        let haveResponsedAll: boolean = false;
        let haveAddedItem: boolean = false;

        const itemsCount = planCheckTrackerItemData?.length;
        const partsCount = planCheckTrackerPartData?.length;
        const partItemsCount = planCheckTrackerPartData?.filter(part => part?.item_list?.length !== 0)?.length;

        const itemNotResponseCount = planCheckTrackerItemData?.filter(item => item?.architect_response === null)?.length;

        const partItemNotResponseCount = planCheckTrackerPartData.filter(part =>
            part.item_list && part.item_list.some(item => item.architect_response === null)
        ).length;

        if (planCheckTrackerType === 'Basic') {
            if (itemsCount === 0) {
                haveAddedItem = false;
            } else {
                haveAddedItem = true;
            }

            if (itemNotResponseCount === 0) {
                haveResponsedAll = true;
            } else {
                haveResponsedAll = false;
            }
        } else if (planCheckTrackerType === 'Detail') {
            if (partItemsCount === 0 || partsCount === 0) {
                haveAddedItem = false;
            } else {
                haveAddedItem = true;
            }

            if (partItemNotResponseCount === 0) {
                haveResponsedAll = true;
            } else {
                haveResponsedAll = false;
            }
        }

        if (haveAddedItem === true && haveResponsedAll === true) {
            setShowArcSignatureModal(true);
        } else {
            if (haveAddedItem === false) {
                confirmDialog({
                    message: `It looks like you haven't created any items in the document. Please create at least one item and respond to it to publish the document.`,
                    header: 'Information',
                    icon: 'bi bi-info-circle',
                    defaultFocus: 'accept',
                    acceptClassName: 'p-button-success',
                    accept: () => navigate(-1),
                    acceptLabel: 'Ok',
                    rejectLabel: 'Close',
                    resizable: false,
                    draggable: false,
                });
            }

            if (haveResponsedAll === false) {
                confirmDialog({
                    message: `Please make sure your responses are complete before publishing.`,
                    header: 'Information',
                    icon: 'bi bi-info-circle',
                    defaultFocus: 'accept',
                    acceptClassName: 'p-button-success',
                    accept: () => navigate(-1),
                    acceptLabel: 'Ok',
                    rejectLabel: 'Close',
                    resizable: false,
                    draggable: false,
                });
            }
        }
    };

    // add sign database functions
    function checkArcCredentials(): boolean {
        const item = checkText(arcSignature, true, setArcSignatureError);
        return item;
    }

    useEffect(() => {
        checkText(arcSignature, false, setArcSignatureError);
    }, [arcSignature]);

    const signAndPublishByArc = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkArcCredentials()) {
            setLoading(true);

            const data = await signAndPublishPlanCheckTrackerByArc(planCheckTrackerData?.id!, 1, arcSignature);

            setLoading(false);
            if (data.statusCode === 200) {
                planCheckTrackerDataGet()
                handleCloseArcSignatureModal();

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "The plan check tracker has been signed and published successfully.",
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "An error occurred while signing and publishing the plan check tracker.",
                        life: 3000,
                    });
                }
            }
        }
    };

    const arcSignatureModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">Sign and publish plan check tracker</h5>

            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseArcSignatureModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const arcSignatureModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={handleCloseArcSignatureModal}
            />
            <Button
                loading={loading}
                label={loading ? "Processing" : "Submit"}
                className="custom_btn primary"
                onClick={signAndPublishByArc}
            />
        </div>
    );

    /* *********** Building inspector *********** */
    const handleCloseBiSignatureModal = () => {
        setShowBiSignatureModal(false);
        setBiSignature("");
        setBiSignatureError('');
    };

    const handleSignAndPublishByBI = () => {
        const planCheckTrackerType = planCheckTrackerData?.check_type;
        let haveResponsedAll: boolean = false;

        const partItemNotResponseCount = planCheckTrackerPartData.filter(part =>
            part.item_list && part.item_list.some(item => item.building_inspector_response === null)
        ).length;

        const itemNotResponseCount = planCheckTrackerItemData?.filter(item => item?.building_inspector_response === null)?.length;

        if (planCheckTrackerType === 'Basic') {
            if (itemNotResponseCount === 0) {
                haveResponsedAll = true;
            } else {
                haveResponsedAll = false;
            }
        } else if (planCheckTrackerType === 'Detail') {
            if (partItemNotResponseCount === 0) {
                haveResponsedAll = true;
            } else {
                haveResponsedAll = false;
            }
        }

        if (haveResponsedAll === true) {
            setShowBiSignatureModal(true);
        } else {
            confirmDialog({
                message: 'Please make sure your responses are complete before publishing.',
                header: 'Information',
                icon: 'bi bi-info-circle',
                defaultFocus: 'accept',
                acceptClassName: 'p-button-success',
                accept: () => navigate(-1),
                acceptLabel: 'Ok',
                rejectLabel: 'Close',
                resizable: false,
                draggable: false,
            });
        }
    };

    // add sign database functions
    function checkBiCredentials(): boolean {
        const item = checkText(biSignature, true, setBiSignatureError);
        return item;
    }

    useEffect(() => {
        checkText(biSignature, false, setBiSignatureError);
    }, [biSignature]);

    // Sign and publish api
    const signAndPublishByBI = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkBiCredentials()) {
            setLoading(true);

            const data = await signAndPublishPlanCheckTrackerByBI(planCheckTrackerData?.id!, 1, biSignature);

            setLoading(false);
            if (data.statusCode === 200) {
                planCheckTrackerDataGet()
                handleCloseBiSignatureModal();

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "The plan check tracker has been signed and published successfully.",
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "An error occurred while signing and publishing the plan check tracker.",
                        life: 3000,
                    });
                }
            }
        }
    };

    const biSignatureModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">Sign and publish plan check tracker</h5>

            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseBiSignatureModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const biSignatureModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={handleCloseBiSignatureModal}
            />
            <Button
                loading={loading}
                label={loading ? "Processing" : "Publish"}
                className="custom_btn primary"
                onClick={signAndPublishByBI}
            />
        </div>
    );
    /* *************************************************************************** */


    /* ********************* Response create by building inspector *************** */
    const handleCreateResponse = () => {
        confirmDialog({
            message: 'Are you sure, Do you want to create a new document to respond to the plan check tracker?',
            header: 'Confirmation',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            accept: () => createResponse(),
            resizable: false,
            draggable: false,
        });
    }

    const createResponse = async () => {
        const data = await createPlanCheckTrackerResponse(planCheckTrackerData?.id!);

        if (data.statusCode === 200) {
            planCheckTrackerDataGet()
            setLoading(false);

            if (toast.current) {
                toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: "The response was successfully created.",
                    life: 3000,
                });
            }
            setTimeout(() => {
                navigate(`/project/detail/default-service/pre-commencement/plan-check-tracker/list`, { state: { data: projectData } });
            }, 500);
        } else {
            setLoading(false);
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "An error occurred while creating the response.",
                    life: 3000,
                });
            }
        }
    }
    /* *************************************************************************** */



    /* *********************************** Mail ********************************** */
    const handleCloseMailModal = () => {
        setShowMailModal(false);
        setEmailAddress("");
    };

    const handleAddMail = () => {
        setShowMailModal(true);
    };

    // need to add email for send report.
    // validations.
    function checkCredentialsEmail(): boolean {
        const mail = checkEmail(emailAddress, true, setEmailAddressError);
        return mail;
    }

    // clear message
    useEffect(() => {
        checkEmail(emailAddress, false, setEmailAddressError);
    }, [emailAddress]);

    // database error set message.
    function showAPIErrorEmail(keyName: string, error: string): void {
        if (keyName === "email") {
            setEmailAddressError(error);
        }
    }

    // send mail database functions.
    const handleSendMail = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentialsEmail()) {
            setLoading(true);
            const data = await sharePlanCheck(planCheckTrackerData?.id!, emailAddress);

            setLoading(false);
            if (data.statusCode === 200) {
                planCheckTrackerDataGet()
                handleCloseMailModal();

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "Email sent successfully.",
                        life: 3000,
                    });
                }
            } else if (data.statusCode === 422) {
                // database error
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorEmail(key, value[0])
                );
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error sending the email.",
                        life: 3000,
                    });
                }
            }
        }
    };

    const mailModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">Send report via mail</h5>
            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseMailModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const mailModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={handleCloseMailModal}
            />
            <Button
                loading={loading}
                label={`${loading ? "Sending" : "Send"}`}
                className="custom_btn primary"
                onClick={handleSendMail}
            />
        </div>
    );

    const handleViewItemFile = (fileUrl: string) => {
        if (fileUrl && fileUrl !== "") {
            setShowItemDocViewModal(true);
            setFileUrl(fileUrl);
        }
    };

    const handleCloseFileViewModal = () => {
        setShowItemDocViewModal(false);
    };
    /* *************************************************************************** */


    /* ******************************** Download ********************************* */
    const handleDownloadReport = async () => {
        if (isDownloading) return;
        // const reportPath = baseURL +"/"+ planCheckData?.file_download_path;
        const reportPath = planCheckTrackerData?.file_download_path;
        if (!reportPath) {
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "File not found!",
                    life: 3000,
                });
            }
            return;
        }

        setIsDownloading(true);

        setTimeout(async () => {
            try {
                const response = await fetch(reportPath);

                if (!response.ok) {
                    throw new Error("Failed to fetch the file.");
                }

                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = reportPath.split("/").pop() || "plan check tracker report.pdf";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "File downloaded successfully.",
                        life: 3000,
                    });
                }
            } catch (error) {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "Error downloading the file",
                        life: 3000,
                    });
                }
            } finally {
                setIsDownloading(false);
            }
        }, 500);
    };

    const handleViewReportDocument = () => {
        if (planCheckTrackerData?.file_download_path) {
            setShowItemDocViewModal(true);
            setFileUrl(planCheckTrackerData?.file_download_path);
        }
    };
    /* *************************************************************************** */


    /* **************************** Common functions ***************************** */
    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* *************************************************************************** */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content full_screen_view">
                {loadingPage ? (
                    <div className="loading_container">
                        <ProgressSpinner className="progresscircle custom" style={{ width: '50px', height: '50px', stroke: '#8953D7' }} strokeWidth="8" fill="var(--surface-ground)" animationDuration="1s" />
                    </div>
                ) : error ? (
                    <ErrorPage errorMessage={error} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-3)}>
                                    {projectData?.job_number}: {projectData?.address}
                                </li>

                                <li className="page_breadcrumb_item has_child">
                                    {projectData?.folder}
                                </li>

                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-2)}>
                                    {projectData?.feature_name}
                                </li>

                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    Plan check tracker list
                                </li>

                                <li className="page_breadcrumb_item has_child">
                                    Plan check tracker - {planCheckTrackerData?.check_type}
                                </li>
                                <li className="page_breadcrumb_item active">Plan check tracker Report</li>
                            </ul>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_header_area justify-content-end mt-sm-3 mt-2">
                            <div className="page_action_area">
                                {loginUserRoleName === 'building inspector' && (
                                    <React.Fragment>
                                        {planCheckTrackerData?.is_created_responce === null && planCheckTrackerData?.stage_for === 'Original' ? (
                                            <button
                                                className="add_data_link_btn p-ripple"
                                                onClick={handleCreateResponse}
                                            >
                                                <i className="bi bi-reply"></i>
                                                <span>Response</span>
                                                <Ripple />
                                            </button>
                                        ) : (isSignedAndPublishedByBi === false && planCheckTrackerData?.is_created_responce === null && hasPermissionAddSignature) ? (
                                            <button
                                                className="add_data_link_btn p-ripple"
                                                onClick={handleSignAndPublishByBI}
                                            >
                                                <i className="bi bi-send"></i>
                                                <span>Sign & Publish</span>
                                                <Ripple />
                                            </button>
                                        ) : null}
                                    </React.Fragment>
                                )}

                                {loginUserRoleName === 'architect' && (
                                    <React.Fragment>
                                        {isSignedAndPublishedByArc === false && hasPermissionAddSignature && (
                                            <button
                                                className="add_data_link_btn p-ripple"
                                                onClick={handleSignAndPublishByARC}
                                            >
                                                <i className="bi bi-send"></i>
                                                <span>Sign & Publish</span>
                                                <Ripple />
                                            </button>
                                        )}
                                    </React.Fragment>
                                )}

                                {planCheckTrackerData?.file_download_path !== null && (
                                    <React.Fragment>
                                        {hasPermissionDownloadFile && (
                                            <button
                                                className="add_data_link_btn p-ripple"
                                                onClick={handleDownloadReport}
                                                disabled={isDownloading}
                                            >
                                                {isDownloading ? (
                                                    <ProgressSpinner
                                                        style={{ width: "20px", height: "20px" }}
                                                        strokeWidth="5"
                                                        fill="none"
                                                        animationDuration="1s"
                                                        className="spin_loader"
                                                    />
                                                ) : (
                                                    <i className="bi bi-download"></i>
                                                )}
                                                <span>{isDownloading ? "Downloading" : "Download"}</span>
                                                <Ripple />
                                            </button>
                                        )}

                                        {loginUserRoleName === 'building inspector' && (
                                            <button
                                                className="add_data_link_btn p-ripple"
                                                onClick={handleAddMail}
                                            >
                                                <i className="bi bi-envelope"></i>
                                                <span>Send</span>
                                                <Ripple />
                                            </button>
                                        )}
                                    </React.Fragment>
                                )}
                            </div>
                        </div>

                        {planCheckTrackerData && planCheckTrackerData !== null ? (
                            <article className="site_visit_report_container">
                                <div className="sv_report_header">
                                    <div className="sv_report_header_sub">
                                        <div className="sv_report_header_content">
                                            <h5>Project:&nbsp;
                                                <span>{projectData ? (projectData?.job_number + ': ' + projectData?.address) : '----'}</span>
                                            </h5>
                                        </div>
                                    </div>
                                    <div className="sv_report_header_sub">
                                        <div className="sv_report_header_content">
                                            <h5>Description:&nbsp;
                                                <span>{(projectData && projectData?.description) ? projectData?.description : '----'}</span>
                                            </h5>
                                        </div>
                                    </div>
                                    <div className="sv_report_header_sub">
                                        <div className="sv_report_header_content">
                                            <h5>Date:&nbsp;
                                                <span>{formatDate(today)}</span>
                                            </h5>
                                        </div>
                                        <div className="sv_report_header_content">
                                            <h5>Inspector name:&nbsp;
                                                <span>{planCheckTrackerData.inspector_name}</span>
                                            </h5>
                                        </div>
                                    </div>
                                    <div className="sv_report_header_sub mb-0">
                                        <div className="sv_report_header_content">
                                            <h5>Stage:&nbsp;
                                                <span>{planCheckTrackerData?.stage}</span>
                                            </h5>
                                        </div>
                                    </div>
                                </div>

                                <div className="sv_report_body">
                                    {/* <div className="checklist_status_header">
                                        <h5 className="text_warning">Open: {String(0).padStart(2, '0')}</h5>
                                        <h5 className="text_success">Closed: {String(0).padStart(2, '0')}</h5>
                                    </div> */}

                                    <div className="status_lable_container">
                                        <div className="status_lable_area p-0 mb-3">
                                            <div className="status_lable">
                                                <span style={{ backgroundColor: '#FFEFBC' }}></span>
                                                <h6>Open</h6>
                                            </div>
                                            <div className="status_lable">
                                                <span style={{ backgroundColor: '#B9F6CA' }}></span>
                                                <h6>Closed</h6>
                                            </div>
                                        </div>
                                    </div>

                                    {/* For Detail plan check tracker */}
                                    {planCheckTrackerData?.check_type === 'Detail' ? (
                                        <React.Fragment>
                                            {planCheckTrackerPartData && planCheckTrackerPartData?.length > 0 ? (
                                                <div className="sv_report_table_area table-responsive pl_table_area">
                                                    <table className="table table-bordered sv_report_table pl_table mb-0">
                                                        <thead>
                                                            <tr className="sv_report_table_header light">
                                                                <th style={{ width: '5%' }} className="item_code_column">Item code</th>
                                                                <th style={{ width: '80%' }} className="comment_column">Comment</th>
                                                                <th style={{ width: '15%' }} className="files_column">Files</th>
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {planCheckTrackerPartData?.map((part) => (
                                                                <React.Fragment>
                                                                    <tr key={part?.id} className="sv_report_table_data data_group_row">
                                                                        <td colSpan={4}>
                                                                            {part?.part_number + ' - ' + part?.part_title}
                                                                        </td>
                                                                    </tr>

                                                                    {part?.item_list && part?.item_list?.length > 0 && (
                                                                        <React.Fragment>
                                                                            {part?.item_list?.map((item) => (
                                                                                <tr key={item?.id} className="sv_report_table_data inner_tabel_row" style={{ backgroundColor: item?.status === 'Open' ? '#FFEFBC' : '#B9F6CA' }}>
                                                                                    <td style={{ width: '5%' }} className="item_code_column">
                                                                                        {item?.item_code}
                                                                                    </td>

                                                                                    <td style={{ width: '80%' }} className="comment_column">
                                                                                        {item?.comment}
                                                                                    </td>

                                                                                    <td style={{ width: '15%' }} className="files_column">
                                                                                        <div className="item_file_area mt-1">
                                                                                            {item && item?.file_list && item?.file_list?.length > 0 ? (
                                                                                                <React.Fragment>
                                                                                                    {item?.file_list?.map((file) => {
                                                                                                        return (
                                                                                                            <React.Fragment>
                                                                                                                {file?.extension === 'pdf' ? (
                                                                                                                    <div className="item_file_label">
                                                                                                                        <div className="item_doc p-ripple"
                                                                                                                            onClick={() => handleViewItemFile(baseURL + file?.path!)}>
                                                                                                                            <i className="bi bi-file-earmark-pdf"></i>
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                ) : (
                                                                                                                    <div className="item_file_label">
                                                                                                                        <Image
                                                                                                                            src={`${baseURL + file?.path}`}
                                                                                                                            alt={`Image`}
                                                                                                                            className="item_image"
                                                                                                                            preview
                                                                                                                        />
                                                                                                                    </div>
                                                                                                                )}
                                                                                                            </React.Fragment>
                                                                                                        )
                                                                                                    })}
                                                                                                </React.Fragment>
                                                                                            ) : (
                                                                                                <div className="no_file_found">
                                                                                                    ----
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </React.Fragment>
                                                                    )}
                                                                </React.Fragment>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <div className="no_report_data">
                                                    <h6>
                                                        <i className="bi bi-exclamation-circle me-1"></i>
                                                        No data found!
                                                    </h6>
                                                </div>
                                            )}
                                        </React.Fragment>
                                    ) : planCheckTrackerData?.check_type === 'Basic' ? (
                                        <React.Fragment>
                                            {planCheckTrackerItemData && planCheckTrackerItemData?.length > 0 ? (
                                                <div className="sv_report_table_area table-responsive pl_table_area">
                                                    <table className="table table-bordered sv_report_table pl_table mb-0">
                                                        <thead>
                                                            <tr className="sv_report_table_header light">
                                                                <th style={{ width: '5%' }} className="item_code_column">Item code</th>
                                                                <th style={{ width: '80%' }} className="comment_column">Comment</th>
                                                                <th style={{ width: '15%' }} className="files_column">Files</th>
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {planCheckTrackerItemData?.map((item) => (
                                                                <tr key={item?.id} className="sv_report_table_data inner_tabel_row" style={{ backgroundColor: item?.status === 'Open' ? '#FFEFBC' : '#B9F6CA' }}>
                                                                    <td style={{ width: '5%' }} className="item_code_column">
                                                                        {item?.item_code}
                                                                    </td>
                                                                    <td style={{ width: '80%' }} className="comment_column">
                                                                        {item?.comment}
                                                                    </td>
                                                                    <td style={{ width: '15%' }} className="files_column">
                                                                        <div className="item_file_area mt-1">
                                                                            {item && item?.file_list && item?.file_list?.length > 0 ? (
                                                                                <React.Fragment>
                                                                                    {item?.file_list?.map((file) => {
                                                                                        return (
                                                                                            <React.Fragment>
                                                                                                {file?.extension === 'pdf' ? (
                                                                                                    <div className="item_file_label">
                                                                                                        <div className="item_doc p-ripple"
                                                                                                            onClick={() => handleViewItemFile(baseURL + file?.path!)}>
                                                                                                            <i className="bi bi-file-earmark-pdf"></i>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    <div className="item_file_label">
                                                                                                        <Image
                                                                                                            src={`${baseURL + file?.path}`}
                                                                                                            alt={`Image`}
                                                                                                            className="item_image"
                                                                                                            preview
                                                                                                        />
                                                                                                    </div>
                                                                                                )}
                                                                                            </React.Fragment>
                                                                                        )
                                                                                    })}
                                                                                </React.Fragment>
                                                                            ) : (
                                                                                <div className="no_file_found">
                                                                                    ----
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <div className="no_report_data">
                                                    <h6>
                                                        <i className="bi bi-exclamation-circle me-1"></i>
                                                        No data found!
                                                    </h6>
                                                </div>
                                            )}
                                        </React.Fragment>
                                    ) : null}

                                    {(isSignedAndPublishedByBi === true || isSignedAndPublishedByArc === true) && (
                                        <div className="sv_report_footer">
                                            <div className="sv_report_footer_item text-start">
                                                {isSignedAndPublishedByBi === true && planCheckTrackerData?.stage_for === 'Response' && (
                                                    <React.Fragment>
                                                        <h4>Building Inspector</h4>
                                                        <h3>{planCheckTrackerData?.sign_by_bi}</h3>
                                                        {/* <h5>Prepared by</h5> */}
                                                        <h6>{planCheckTrackerData?.sign_date_by_bi}</h6>
                                                    </React.Fragment>
                                                )}
                                            </div>

                                            <div className="sv_report_footer_item">
                                                {isSignedAndPublishedByArc === true && planCheckTrackerData?.stage_for === 'Original' && (
                                                    <React.Fragment>
                                                        <h4>Architect</h4>
                                                        <h3>{planCheckTrackerData?.sign_by_arc}</h3>
                                                        {/* <h5>Prepared by</h5> */}
                                                        <h6>{planCheckTrackerData?.sign_date_by_arc}</h6>
                                                    </React.Fragment>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </article>
                        ) : (
                            <div className="page_main_content_section no_header">
                                <NoDataCreated
                                    message={`No data found!`}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>


            {/* Mail modal */}
            <Dialog
                header={mailModalHeader}
                footer={mailModalFooter}
                headerClassName="custom_modal_header"
                visible={showMailModal}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseMailModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-3 pb-sm-0 w-100">
                        <div className="row">
                            <div className="col-12">
                                <TextInput
                                    id="emailAddress"
                                    label="E-mail address"
                                    labelHtmlFor="emailAddress"
                                    required={true}
                                    value={emailAddress}
                                    placeholder="abcd@gmail.com"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setEmailAddress(e.target.value)
                                    }
                                    error={emailAddressError}
                                    keyFilter={"email"}
                                    formGroupClassName="mb-2"
                                />

                                {planCheckTrackerData?.file_download_path !== null && (
                                    <div
                                        className="file_attached_area"
                                        onClick={handleViewReportDocument}
                                    >
                                        <i className="bi bi-paperclip"></i>
                                        <h6>Plan check tracker report.pdf</h6>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Architect sign and publish modal */}
            <Dialog
                visible={showArcSignatureModal}
                header={arcSignatureModalHeader}
                footer={arcSignatureModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseArcSignatureModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-3 pb-sm-0 w-100">
                        <div className="row">
                            <div className="col-12">
                                <div className="page_form_group">
                                    <Message
                                        severity='info'
                                        className="message_text"
                                        icon="bi bi-info-circle"
                                        style={{ border: '1px solid', borderRadius: '8px' }}
                                        text={`Once you sign and publish this report, it will automatically be shared with the building inspector, who will be able to view and respond to it.`}
                                    />
                                </div>
                            </div>

                            <div className="col-12">
                                <div className="page_form_group">
                                    <label htmlFor="arcSignature" className="custom_form_label is_required">
                                        Signature
                                    </label>

                                    <InputText
                                        id="arcSignature"
                                        value={arcSignature}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setArcSignature(e.target.value)}
                                        className="custom_form_input signature_input"
                                        placeholder="Enter your signature"
                                        autoFocus
                                        type="text"
                                    />

                                    {arcSignatureError && (
                                        <small className="form_error_msg">{arcSignatureError}</small>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Building inspector sign and publish modal */}
            <Dialog
                visible={showBiSignatureModal}
                header={biSignatureModalHeader}
                footer={biSignatureModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseBiSignatureModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-3 pb-sm-0 w-100">
                        <div className="row">
                            <div className="col-12">
                                <div className="page_form_group">
                                    <Message
                                        severity='info'
                                        className="message_text"
                                        icon="bi bi-info-circle"
                                        style={{ border: '1px solid', borderRadius: '8px' }}
                                        text={`Once you sign and publish this report, it will automatically be shared with the architect, who will be able to view and respond to it.`}
                                    />
                                </div>
                            </div>

                            <div className="col-12">
                                <div className="page_form_group">
                                    <label
                                        htmlFor="biSignature"
                                        className="custom_form_label is_required"
                                    >
                                        Signature
                                    </label>

                                    <InputText
                                        id="biSignature"
                                        value={biSignature}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBiSignature(e.target.value)}
                                        className="custom_form_input signature_input"
                                        placeholder="Enter your signature"
                                        autoFocus
                                        type="text"
                                    />

                                    {biSignatureError && (
                                        <small className="form_error_msg">{biSignatureError}</small>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Document view modal */}
            <DocumentViewModal
                isOpen={showItemDocViewModal}
                onClose={handleCloseFileViewModal}
                fileUrl={fileUrl}
            />
            {/*  */}
        </React.Fragment>
    )
}

export default PlanCheckTrackerReport;